import { useState } from 'react';
import type { ProjectTask } from '@/types/odoo';
import type { FaseGroup } from '@/utils/materiaal';
import { MateriaalUploadControl } from './MateriaalUploadControl';
import { FaseOpgelijstToggle } from './FaseOpgelijstToggle';
import { MateriaalPickingTable } from './MateriaalPickingTable';

interface MateriaalFaseGroupProps {
  fase: FaseGroup;
  defaultOpen: boolean;
  isUploading: boolean;
  isEasykit?: boolean;
  childTasks: ProjectTask[];
  getOpgelijstValue: (taskId: number) => boolean;
  onToggleOpgelijst: (taskId: number, value: boolean) => Promise<void>;
  onUpload: (file: File) => Promise<void>;
  onCadClick: () => void;
  onDelete: (pickingId: number) => Promise<void>;
  onError: (msg: string) => void;
  /** Capability flags — default all to `allowUpload` so a single
   *  `canFeature('csv_upload')` value reproduces today's gating. */
  allowUpload: boolean;
  allowDelete?: boolean;
  allowOpgelijst?: boolean;
  allowCad?: boolean;
}

/**
 * Composite for ONE fase: header/count + upload control + opgelijst controls +
 * the picking tables. Driven entirely by props + capability flags. This is the
 * piece Fase 6b will reuse on the dashboard. Only the open/closed disclosure
 * state is local (matching the original FaseSection); all data + persistence
 * arrive via props.
 */
export function MateriaalFaseGroup({
  fase,
  defaultOpen,
  isUploading,
  isEasykit,
  childTasks,
  getOpgelijstValue,
  onToggleOpgelijst,
  onUpload,
  onCadClick,
  onDelete,
  onError,
  allowUpload,
  allowDelete,
  allowOpgelijst,
  allowCad,
}: MateriaalFaseGroupProps) {
  const [open, setOpen] = useState(defaultOpen);

  // Default each capability to allowUpload so one csv_upload-derived flag
  // reproduces the original gating exactly.
  const canDelete = allowDelete ?? allowUpload;
  const canToggle = allowOpgelijst ?? allowUpload;
  const canCad = allowCad ?? allowUpload;

  const header = (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className="flex-1 text-left rounded-xl border border-gray-200 bg-white hover:border-td-blue/40 hover:shadow-sm p-3 transition-all cursor-pointer"
    >
      <div className="flex items-center gap-2">
        <svg
          className={`w-4 h-4 text-td-muted shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <p className="text-sm font-bold text-td-navy truncate flex-1 min-w-0">
          {fase.taskName}
        </p>
        <span className="shrink-0 text-[10px] font-bold text-td-muted bg-gray-100 px-1.5 py-0.5 rounded">
          {fase.pickingGroups.length} {fase.pickingGroups.length === 1 ? 'materiaallijst' : 'materiaallijsten'}
        </span>
        <FaseOpgelijstToggle
          taskId={fase.taskId}
          isEasykit={!!isEasykit}
          childTasks={childTasks}
          canToggle={canToggle}
          getOpgelijstValue={getOpgelijstValue}
          onToggle={onToggleOpgelijst}
          variant="header"
        />
      </div>
    </button>
  );

  const body = open ? (
    <div className="mt-2 ml-6 space-y-1.5">
      <FaseOpgelijstToggle
        taskId={fase.taskId}
        isEasykit={!!isEasykit}
        childTasks={childTasks}
        canToggle={canToggle}
        getOpgelijstValue={getOpgelijstValue}
        onToggle={onToggleOpgelijst}
        variant="children"
      />
      {fase.pickingGroups.map((pg, index) => (
        <MateriaalPickingTable
          key={pg.picking.id}
          group={pg}
          defaultOpen={defaultOpen && index === 0}
          canDelete={canDelete}
          onDelete={() => onDelete(pg.picking.id)}
          moves={pg.moves}
        />
      ))}
    </div>
  ) : null;

  return (
    <MateriaalUploadControl
      faseId={fase.taskId}
      faseName={fase.taskName}
      canUpload={allowUpload}
      allowCad={canCad}
      isUploading={isUploading}
      onUpload={onUpload}
      onCadClick={onCadClick}
      onError={onError}
      header={header}
      body={body}
    />
  );
}
